import { useState } from "react";
import { Snowflake, Zap } from "lucide-react";

const THUNDERSTORM_CRITERIA = [
  {
    type: "Thundery Showers",
    color: "#E6D8AD",
    lightningRisk: "Very Low",
    lightningFrequency: "1–2",
    possibleHazards: "Small hail, gusty winds",
    otherPhenomena: "None",
  },
  {
    type: "Weak",
    color: "#A8CFB6",
    lightningRisk: "Low",
    lightningFrequency: "2–3",
    possibleHazards: "Small hail, localised flooding",
    otherPhenomena: "Isolated cells",
  },
  {
    type: "Moderate",
    color: "#E9D85A",
    lightningRisk: "Moderate",
    lightningFrequency: "4–5",
    possibleHazards: "Moderate hail, gusty winds, flooding",
    otherPhenomena: "MCS possible",
  },
  {
    type: "Strong",
    color: "#F89C27",
    lightningRisk: "High",
    lightningFrequency: "5–7",
    possibleHazards: "Large hail, damaging winds, flash flooding",
    otherPhenomena: "MCS, multicell clusters",
  },
  {
    type: "Severe",
    color: "#F33D14",
    lightningRisk: "Very High",
    lightningFrequency: "7–9",
    possibleHazards: "Very large hail, destructive winds, tornadoes",
    otherPhenomena: "MCS, supercells",
  },
  {
    type: "Violent",
    color: "#B45AA9",
    lightningRisk: "Extreme",
    lightningFrequency: "9–10",
    possibleHazards: "Giant hail, catastrophic winds, significant tornadoes",
    otherPhenomena: "Supercells, derecho",
  },
];

export default function CriteriaPage() {
  const [activeTab, setActiveTab] = useState("thunderstorm");

  return (
    <div className="animate-fade-in" data-testid="criteria-page">
      {/* Header */}
      <section className="border-b border-white/5 py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#F9FAFB] uppercase tracking-tight mb-2">
            Outlook Criteria
          </h1>
          <p className="text-[#9CA3AF] max-w-2xl text-sm">
            Classification criteria used in UKWX outlooks.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-white/5 bg-[#111827]/50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Mobile: dropdown select */}
          <div className="md:hidden py-3">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full text-xs font-medium uppercase tracking-widest bg-[#0d1117] border border-white/10 text-[#F9FAFB] px-4 py-2.5 focus:outline-none focus:border-[#FBBF24]/40 cursor-pointer"
              data-testid="criteria-tab-select"
            >
              <option value="thunderstorm">⚡ Thunderstorm</option>
              <option value="snowfall">❄ Snowfall</option>
            </select>
          </div>
          {/* Desktop: horizontal tab buttons */}
          <div className="hidden md:flex gap-0">
            <button
              onClick={() => setActiveTab("thunderstorm")}
              className={`flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-widest font-medium border-b-2 transition-colors ${
                activeTab === "thunderstorm"
                  ? "border-[#FBBF24] text-[#FBBF24]"
                  : "border-transparent text-[#9CA3AF] hover:text-[#F9FAFB]"
              }`}
              data-testid="criteria-tab-thunderstorm"
            >
              <Zap className="w-3.5 h-3.5" />
              Thunderstorm
            </button>
            <button
              onClick={() => setActiveTab("snowfall")}
              className={`flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-widest font-medium border-b-2 transition-colors ${
                activeTab === "snowfall"
                  ? "border-[#7DD3FC] text-[#7DD3FC]"
                  : "border-transparent text-[#9CA3AF] hover:text-[#F9FAFB]"
              }`}
              data-testid="criteria-tab-snowfall"
            >
              <Snowflake className="w-3.5 h-3.5" />
              Snowfall
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {activeTab === "thunderstorm" && (
            <div className="space-y-6" data-testid="thunderstorm-criteria">
              <div className="space-y-1">
                <h2 className="font-heading text-xl font-bold text-[#F9FAFB] uppercase tracking-tight">
                  Thunderstorm Classification
                </h2>
                <p className="text-[#9CA3AF] text-sm max-w-3xl">
                  Thunderstorm outlooks are classified into six severity types based on the Lightning Activity
                  Index (LAI.v1) and storm organisation.
                </p>
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse text-sm" data-testid="thunderstorm-criteria-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      {["Type", "Risk of Lightning", "Lightning Frequency", "Possible Hazards", "Other Phenomena"].map(
                        (heading) => (
                          <th
                            key={heading}
                            className="py-3 px-4 text-left text-[10px] uppercase tracking-widest text-[#6B7280] font-medium"
                          >
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {THUNDERSTORM_CRITERIA.map((row) => (
                      <tr key={row.type} className="border-b border-white/5 hover:bg-[#111827]/60 transition-colors">
                        <td className="py-3 px-4">
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wide font-semibold border"
                            style={{
                              color: row.color,
                              borderColor: `${row.color}50`,
                              backgroundColor: `${row.color}15`,
                            }}
                          >
                            <span className="w-2 h-2" style={{ backgroundColor: row.color }} />
                            {row.type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-[#9CA3AF]">{row.lightningRisk}</td>
                        <td className="py-3 px-4 font-mono text-xs text-[#F9FAFB]">{row.lightningFrequency}</td>
                        <td className="py-3 px-4 text-xs text-[#9CA3AF]">{row.possibleHazards}</td>
                        <td className="py-3 px-4 text-xs text-[#6B7280]">{row.otherPhenomena}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {THUNDERSTORM_CRITERIA.map((row) => (
                  <div key={row.type} className="bg-[#111827] border border-white/10 p-4 space-y-3">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wide font-semibold border"
                      style={{
                        color: row.color,
                        borderColor: `${row.color}50`,
                        backgroundColor: `${row.color}15`,
                      }}
                    >
                      <span className="w-2 h-2" style={{ backgroundColor: row.color }} />
                      {row.type}
                    </span>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div>
                        <dt className="text-[#6B7280] uppercase tracking-wider text-[9px]">Risk of Lightning</dt>
                        <dd className="text-[#9CA3AF] mt-0.5">{row.lightningRisk}</dd>
                      </div>
                      <div>
                        <dt className="text-[#6B7280] uppercase tracking-wider text-[9px]">Lightning Frequency</dt>
                        <dd className="text-[#F9FAFB] font-mono mt-0.5">{row.lightningFrequency}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-[#6B7280] uppercase tracking-wider text-[9px]">Possible Hazards</dt>
                        <dd className="text-[#9CA3AF] mt-0.5">{row.possibleHazards}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-[#6B7280] uppercase tracking-wider text-[9px]">Other Phenomena</dt>
                        <dd className="text-[#6B7280] mt-0.5">{row.otherPhenomena}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>

              {/* LAI Legend */}
              <div className="bg-[#111827] border border-white/10 p-5 space-y-3">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  LAI.v1 Scale
                </h3>
                <p className="text-[#9CA3AF] text-sm max-w-2xl">
                  The Lightning Activity Index (LAI.v1) is a 0–10 scale indicating the intensity of expected lightning activity. Values of 1–3
                  indicate weak activity, 4–6 moderate, 7–9 severe, and 10 extreme or violent convection.
                </p>
                <div className="flex w-full max-w-xs">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <div key={n} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full h-3"
                        style={{
                          backgroundColor:
                            n <= 3 ? "#A8CFB6" : n <= 5 ? "#E9D85A" : n <= 7 ? "#F89C27" : n <= 9 ? "#F33D14" : "#B45AA9",
                        }}
                      />
                      <span className="text-[9px] font-mono text-[#6B7280]">{n}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Severe Phenomena Criteria */}
              <div className="space-y-3" data-testid="severe-phenomena-criteria">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  Severe Phenomena Criteria
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {[
                    { name: "Hail", desc: "Risk of hail exceeding 3cm in diameter" },
                    { name: "Wind", desc: "Risk of gusts exceeding 50mph" },
                    { name: "Flooding", desc: "Elevated flash flood risk" },
                    { name: "MCS", desc: ">20% probability of organised MCS development" },
                    { name: "Tornado", desc: ">10% probability" },
                    { name: "Supercells", desc: ">20% probability of supercell development" },
                  ].map((ph) => (
                    <div key={ph.name} className="bg-[#111827] border border-white/10 p-4 space-y-1.5">
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-[#FBBF24]">
                        {ph.name}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">{ph.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "snowfall" && (
            <div className="space-y-6" data-testid="snowfall-criteria">
              <div className="space-y-1">
                <h2 className="font-heading text-xl font-bold text-[#F9FAFB] uppercase tracking-tight">
                  Snowfall Outlook Criteria
                </h2>
              </div>

              <div className="bg-[#111827] border border-white/10 p-6 max-w-3xl">
                <div className="flex items-start gap-3">
                  <Snowflake className="w-5 h-5 text-[#7DD3FC] flex-shrink-0 mt-0.5" />
                  <p className="text-[#9CA3AF] text-sm leading-relaxed">
                    Snowfall outlooks focus on predicted accumulations, with emphasis on areas below 200 m
                    elevation. Severe phenomena are limited to blizzards, snow melt, and freezing rain.
                  </p>
                </div>
              </div>

              {/* Snowfall totals table */}
              <div className="space-y-3">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  Snowfall Total Categories
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                  {[
                    { value: "0-3cm", label: "0–3 cm", color: "#B0B0B0", desc: "Trace / light" },
                    { value: "1-5cm", label: "1–5 cm", color: "#A3DFFF", desc: "Light" },
                    { value: "5-10cm", label: "5–10 cm", color: "#3B82F6", desc: "Moderate" },
                    { value: "10-15cm", label: "10–15 cm", color: "#1E40AF", desc: "Heavy" },
                    { value: "15-20cm", label: "15–20 cm", color: "#EC4899", desc: "Very heavy" },
                    { value: ">20cm", label: ">20 cm", color: "#9333EA", desc: "Extreme" },
                  ].map((cat) => (
                    <div
                      key={cat.value}
                      className="bg-[#111827] border border-white/10 p-3 space-y-1.5"
                    >
                      <div
                        className="w-full h-1.5"
                        style={{ backgroundColor: cat.color }}
                      />
                      <p
                        className="font-mono text-xs font-bold"
                        style={{ color: cat.color }}
                      >
                        {cat.label}
                      </p>
                      <p className="text-[10px] text-[#6B7280] uppercase tracking-wide">{cat.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Severe phenomena */}
              <div className="space-y-3">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  Severe Phenomena
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl">
                  {[
                    {
                      name: "Blizzards",
                      color: "#F97316",
                      desc: "Heavy snow combined with strong winds (≥ 35 mph), reducing visibility to near zero.",
                    },
                    {
                      name: "Snow Melt",
                      color: "#3B82F6",
                      desc: "Rapid snowmelt leading to localised flooding, particularly on frozen or saturated ground.",
                    },
                    {
                      name: "Freezing Rain",
                      color: "#8B5CF6",
                      desc: "Rain that freezes on contact with sub-zero surfaces, forming black ice. Highly hazardous.",
                    },
                  ].map((ph) => (
                    <div key={ph.name} className="bg-[#111827] border border-white/10 p-4 space-y-2">
                      <span
                        className="inline-flex px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border"
                        style={{
                          color: ph.color,
                          borderColor: `${ph.color}60`,
                          backgroundColor: `${ph.color}15`,
                        }}
                      >
                        {ph.name}
                      </span>
                      <p className="text-xs text-[#6B7280]">{ph.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
